import { StorageManager } from '../../../../../../common/storage-manager.class';
import { environment } from '../../../../../../../environments/environment';
import {
    DropdownData,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    ReactiveFieldLogicContext,
    ReactiveSectionLogicContext,
    DropdownFilterContext,
    ReactiveDropdownCatalogs,
    ReactiveDropdownDataSourceContext,
} from '@fitogest/reactive-form-builder/interfaces';
import { AgroambientalModel } from '../../../../../../models/tareas/agroambientales.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { SectorModel } from '../../../../../../models/form-common/sector.model';
import { ParcelasModel } from '../../../../../../models/form-common/parcelas.model';
import { MaquinariaModel } from '../../../../../../models/trabajadores/maquinaria.model';
import { getDropdownSelectedSingle, list } from '@fitogest/reactive-form-builder/utils';
import {
    createTareasLocationDropdownDataSource,
    getContextEntityId,
    getContextEntityIds,
    tareasLocationDependencyBehaviour,
    tareasLocationFincaDependencyBehaviour,
} from '../../../shared/tareas-location-dropdown.datasource';

interface ActividadSobreCubiertaModel {
    codigo_SIEX?: string | number;
    actividad?: string;
    actividad_sobre_la_cubierta?: string;
}

interface EspecieAnimalModel {
    id?: string | number;
    especies_animales?: string;
}

interface CalendarDateLimits {
    minDate?: Date;
    maxDate?: Date;
    disabledDates?: Date[];
}

interface AnnualCalendarWindow {
    startMonth: number;
    startDay: number;
    endMonth: number;
    endDay: number;
}

export class AgroambientalesFormDefinition {
    public readonly appName = environment.appName;
    public readonly fincaName = environment.appName === 'avafitogest' ? 'Explotación' : environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelasName = environment.features.parcelasName;
    public readonly tipoSuperficieParcelas = environment.features.hasSuperficieCultivada
        ? 'Sup.\xa0Cultivada'
        : 'S.\xa0Sigpac';
    public readonly textoSuperficie = environment.features.textoSuperficie;
    public readonly hasClients = environment.features.hasClients;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';

    public formType!: FormType;
    public model: Partial<AgroambientalModel> = {};
    public formFields!: Form<AgroambientalModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public sectores: DropdownData<SectorModel> = list<SectorModel>(['...', null]);
    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);
    public maquinarias: DropdownData<MaquinariaModel> = list<MaquinariaModel>(['...', null]);
    public actividadesSobreCubierta: DropdownData<ActividadSobreCubiertaModel> = list<ActividadSobreCubiertaModel>(['...', null]);
    public especiesAnimales: DropdownData<EspecieAnimalModel> = list<EspecieAnimalModel>(['...', null]);

    public tipoEcorregimenList: DropdownData<string> = list<string>('...', 'Leñosos', 'Arroz', 'Pastoreo');
    public tareasLabor: DropdownData<string> = list<string>(
        ['...', null],
        'Fangueo',
        'Hacer Márgenes',
        'Trailla',
        'Láser',
        'Cultivador',
        'Rotovator',
        'Multiapero',
        'Paquete de trabajo 1',
        'Paquete de trabajo 2',
        'Desbroce Cursos de Agua',
    );
    public tipoOperacionList: DropdownData<string> = list<string>('', 'Escarda química', 'Desbroce mecánico');

    public fieldNames: FormField<AgroambientalModel>[] = [];
    public fieldLabels: string[] = [];

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();
    private readonly allSections: FormSection<AgroambientalModel>[];

    constructor(type: FormType, model: Partial<AgroambientalModel>) {
        this.formType = +type;
        this.model = model;
        this.ensureDefaultDropdownValues();
        this.allSections = this.getFormSections();

        this.formFields = {
            name: 'tareas-agroambientales',
            header: `Datos de la tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[0] ?? 'Agroambientales'}`,
            type: this.formType,
            sections: this.allSections,
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
            serializer: {
                removeFields: ['pastoreo_recommendation_help'],
            },
        };

    }

    public isPastoreoSelected(): boolean {
        return this.getTipoEcorregimen() === 'Pastoreo';
    }

    public setCatalogDataSources(endpoints: {
        readonly fincas: unknown;
        readonly sectores: unknown;
        readonly parcelas: unknown;
        readonly actividadesSobreCubierta: unknown;
        readonly especiesAnimales: unknown;
        readonly maquinarias: unknown;
    }): void {
        const fincaField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_finca' : 'finca');
        const sectorField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_sector' : 'sector');
        const parcelaField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas');
        const actividadField = this.getFieldByName('tipo_mantenimiento_cv');
        const especieField = this.getFieldByName(this.formType < FormType.DELETION ? 'especie' : 'nombre_especie');
        const maquinariaField = this.getFieldByName(this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria');

        if (fincaField !== undefined) {
            fincaField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, FincasModel>({
                endpoint: endpoints.fincas,
                cacheKey: 'agroambientales-fincas',
                label: 'nombre',
                filterOption: (option, context) => this.isFincaOptionAllowed(option.value, context),
            });
            fincaField.dependencyBehaviour = tareasLocationFincaDependencyBehaviour;
        }

        if (sectorField !== undefined) {
            sectorField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, SectorModel>({
                endpoint: endpoints.sectores,
                cacheKey: 'agroambientales-sectores',
                label: 'nombre',
                dependsOn: ['id_finca'],
                filterOption: (option, context) => this.isSectorOptionForSelectedFinca(option.value, context),
            });
            sectorField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (parcelaField !== undefined) {
            parcelaField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, ParcelasModel>({
                endpoint: endpoints.parcelas,
                cacheKey: 'agroambientales-parcelas',
                label: this.appName === 'avafitogest' ? (parcela) => this.getParcelaLabel(parcela) : 'nombre',
                dependsOn: ['id_finca', 'id_sector'],
                filterOption: (option, context) => this.isParcelaOptionForSelectedLocation(option.value, context),
            });
            parcelaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (actividadField !== undefined) {
            actividadField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, ActividadSobreCubiertaModel>({
                endpoint: endpoints.actividadesSobreCubierta,
                cacheKey: 'agroambientales-actividades-sobre-cubierta',
                label: 'actividad_sobre_la_cubierta',
                distinctBy: 'codigo_SIEX',
            });
            actividadField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (especieField !== undefined) {
            especieField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, EspecieAnimalModel>({
                endpoint: endpoints.especiesAnimales,
                cacheKey: 'agroambientales-especies-animales',
                label: 'especies_animales',
            });
            especieField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }

        if (maquinariaField !== undefined) {
            maquinariaField.dataSource = createTareasLocationDropdownDataSource<AgroambientalModel, MaquinariaModel>({
                endpoint: endpoints.maquinarias,
                cacheKey: 'agroambientales-maquinarias',
                label: 'nombre',
            });
            maquinariaField.dependencyBehaviour = tareasLocationDependencyBehaviour;
        }
    }

    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: this.formType < FormType.DELETION ? ['id_finca', 'id_sector', 'id_parcelas'] : [],
            deferred: this.formType < FormType.DELETION ? ['tipo_mantenimiento_cv', 'especie', 'id_maquinaria'] : [],
            lazy: [],
        };
    }

    private getFormSections(): FormSection<AgroambientalModel>[] {
        const sections: FormSection<AgroambientalModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getLocationFields(),
                order: 1,
            },
            {
                name: 'Cubiertas Vegetales Espontáneas o Sembrada',
                width: 12,
                isVisible: true,
                visibleWhen: this.visibleWhenTipo('Leñosos'),
                fields: this.getLenososFields('Leñosos'),
                order: 2,
            },
            {
                name: 'Cubiertas Inertes (Restos Poda)',
                width: 12,
                isVisible: true,
                visibleWhen: this.visibleWhenTipo('Leñosos'),
                fields: this.getCubiertasInertesFields('Leñosos'),
                order: 3,
            },
            {
                name: 'Pastoreo Extensivo',
                width: 12,
                isVisible: true,
                visibleWhen: this.visibleWhenTipo('Pastoreo'),
                fields: this.getPastoreoExtensivoFields('Pastoreo'),
                order: 4,
            },
            {
                name: 'Pastoreo Siega Sostenible / Islas de Biodiversidad',
                width: 12,
                isVisible: true,
                visibleWhen: this.visibleWhenTipo('Pastoreo'),
                fields: this.getPastoreoIslasFields('Pastoreo'),
                order: 5,
            },
            {
                name: 'Ecorregímenes Arroz',
                width: 12,
                isVisible: true,
                visibleWhen: this.visibleWhenTipo('Arroz'),
                fields: this.getArrozAvaFields('Arroz'),
                order: 6,
            },
            {
                name: 'Arroz',
                width: 12,
                isVisible:
                    this.appName === 'sequiador' ||
                    this.appName === 'SIEX' ||
                    this.formType === FormType.EXPORT,
                visibleWhen: this.visibleWhenTipo('Arroz'),
                fields: this.getArrozFields('Arroz'),
                order: 7,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 8,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLocationFields(): FormField<AgroambientalModel>[] {
        return this.pushFields([
            {
                name: this.formType < FormType.DELETION ? 'id_finca' : 'finca',
                label: this.fincaName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.fincas,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it, context): boolean => this.filterFinca(it.value as FincasModel | null, context),
                        },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions({ isRequired: this.formType < FormType.DELETION }),
                order: 1,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_sector' : 'sector',
                        kind: 'filter'
                    }
                ],
                label: this.sectorName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.MULTISELECT_SEARCH : InputType.ONLY_TEXT,
                    data: this.sectores,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it, context): boolean => this.filterSector(it.value as SectorModel | null, context),
                        },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    },
                    {
                        source: 'id_sector',
                        target: this.formType < FormType.DELETION ? 'id_parcelas' : 'parcelas',
                        kind: 'filter'
                    }
                ],
                label: this.parcelasName,
                input: {
                    type: this.formType < FormType.DELETION ? InputType.MULTISELECT_SEARCH : InputType.ONLY_TEXT,
                    data: this.parcelas,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: {
                            valuePrimaryKey: 'id',
                            filter: (it, context): boolean => this.filterParcela(it.value as ParcelasModel | null, context),
                            summation: {
                                summand: environment.features.hasSuperficieCultivada
                                    ? 'superficie_cultivada'
                                    : 'superficie_sigpac',
                                name: this.tipoSuperficieParcelas,
                                uds: this.textoSuperficie,
                                format: (total: number): string => total.toLocaleString('es-ES', {
                                    useGrouping: true,
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }),
                            },
                        },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions({ isRequired: this.formType < FormType.DELETION }),
                order: 3,
            },
            {
                name: 'tipo_ecorregimen',
                label: 'Tipo de Registro Ecorrégimen',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tipoEcorregimenList,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ]);
    }

    private getLenososFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField(
                'fecha_establecimiento_cv',
                'Fecha Establecimiento Cubierta Vegetal',
                1,
                tipo,
                3,
                this.getLenosCalendarLimits('cv'),
            ),
            this.numericField('anchura_cv', 'Anchura Cubierta Vegetal (m)', 2, tipo),
            {
                name: 'tipo_mantenimiento_cv',
                label: 'Tipo Mantenimiento Cubierta Vegetal',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.actividadesSobreCubierta,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'codigo_SIEX' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createTipoVisibilityDependencies('tipo_mantenimiento_cv'),
                visibleWhen: this.visibleWhenFieldTipo(tipo),
                order: 3,
            },
            this.numericField('anchura_libre_proyeccion_1', 'Anchura Libre Proyeccion Copa (m)', 4, tipo),
        ]);
    }

    private getCubiertasInertesFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField(
                'fecha_establecimiento_ci',
                'Fecha Establecimiento Cubierta Inerte',
                1,
                tipo,
                4,
                this.getLenosCalendarLimits('ci'),
            ),
            this.numericField('anchura_ci', 'Anchura Cubierta Inerte (m)', 2, tipo, 4),
            this.numericField('anchura_libre_proyeccion_2', 'Anchura libre de la Proyección Copa (m)', 3, tipo, 4),
        ]);
    }

    private getPastoreoExtensivoFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_inicio_pastoreo_extensivo', 'Fecha Inicio Pastoreo Extensivo', 1, tipo, 3),
            this.calendarField('fecha_fin_pastoreo_extensivo', 'Fecha Fin Pastoreo Extensivo', 2, tipo, 3),
            {
                name: this.formType < FormType.DELETION ? 'especie' : 'nombre_especie',
                label: 'Especie',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.especiesAnimales,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createTipoVisibilityDependencies(
                    this.formType < FormType.DELETION ? 'especie' : 'nombre_especie',
                ),
                visibleWhen: this.visibleWhenFieldTipo(tipo),
                order: 3,
            },
            this.textField('rega', 'REGA', 4, tipo, 3),
            this.numericField('numero_animales', 'Número de Animales', 5, tipo, 3),
        ]);
    }

    private getPastoreoIslasFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_siega_1', 'Siega: 1er Fecha Corte', 1, tipo, 3),
            this.calendarField('fecha_siega_2', 'Siega: 2do Fecha Corte', 2, tipo, 3),
            this.calendarField('fecha_siega_3', 'Siega: 3er Fecha Corte', 3, tipo, 3),
            this.calendarField('fecha_laboreo', 'Fecha Laboreo', 4, tipo, 3),
            this.calendarField('fecha_siembra', this.appName === 'avafitogest' ? 'Fecha Mantenimiento' : 'Fecha Siembra', 5, tipo, 3),
            this.textAreaField(
                'otras_actividades',
                this.appName === 'avafitogest' ? 'Observaciones Mantenimiento' : 'Otras Actividades',
                6,
                tipo,
                6,
            ),
            this.pastoreoRecommendationField(tipo, 7),
        ]);
    }

    private getArrozAvaFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_siembra', 'Fecha Siembra', 1, tipo, 6),
            this.numericField('dosis_siembra', 'Dosis Siembra', 2, tipo, 6),
            this.calendarField('fecha_arroz', 'Fecha Recolección', 3, tipo, 6),
            this.calendarField('fecha_nivelacion', 'Fecha Nivelación', 4, tipo, 6),
            this.calendarField('fecha_seca_inicio_intermitente', 'Fecha Inicio Seca Intermitente', 5, tipo, 6),
            this.calendarField('fecha_seca_fin_intermitente', 'Fecha Fin Seca Intermitente', 6, tipo, 6),
            this.calendarField(
                'fecha_inicio_inundacion_cultivo',
                'Fecha Inicio Inundación del Cultivo',
                7,
                tipo,
                6,
                this.getInundacionCalendarLimits('inicioCultivo'),
            ),
            this.calendarField(
                'fecha_fin_inundacion_cultivo',
                'Fecha Fin Inundación del Cultivo',
                8,
                tipo,
                6,
                this.getInundacionCalendarLimits('finCultivo'),
            ),
            this.calendarField(
                'fecha_inicio_inundacion_invernal',
                'Fecha Inicio Inundación Invernal',
                9,
                tipo,
                6,
                this.getInundacionCalendarLimits('inicioInvernal'),
            ),
            this.calendarField(
                'fecha_fin_inundacion_invernal',
                'Fecha Fin Inundación Invernal',
                10,
                tipo,
                6,
                this.getInundacionCalendarLimits('finInvernal'),
            ),
        ]);
    }

    private getArrozFields(tipo: string): FormField<AgroambientalModel>[] {
        return this.pushFields([
            this.calendarField('fecha_arroz', 'Fecha Arroz', 1, tipo),
            {
                name: 'labor',
                label: 'Labor',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tareasLabor,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createTipoVisibilityDependencies('labor'),
                visibleWhen: this.visibleWhenFieldTipo(tipo),
                order: 2,
            },
            {
                name: 'tipo_operacion',
                label: 'Tipo Operación',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    data: this.tipoOperacionList,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createTipoVisibilityDependencies('tipo_operacion'),
                visibleWhen: this.visibleWhenFieldTipo(tipo),
                order: 3,
            },
            {
                name: this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria',
                label: 'Maquinaria',
                input: {
                    type: this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    data: this.maquinarias,
                    isEditable: true,
                    ...(this.formType < FormType.DELETION ? {
                        dropdownConfiguration: { valuePrimaryKey: 'id' },
                    } : {}),
                },
                width: 3,
                conditions: this.conditions({ isVisible: false }),
                dependencies: this.createTipoVisibilityDependencies(
                    this.formType < FormType.DELETION ? 'id_maquinaria' : 'maquinaria',
                ),
                visibleWhen: this.visibleWhenFieldTipo(tipo),
                order: 4,
            },
            this.textAreaField('observaciones', 'Observaciones', 5, tipo, 6),
        ]);
    }

    private getHiddenFields(): FormField<AgroambientalModel>[] {
        return [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isVisible: true }),
                order: 1,
            },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.getDefaultClientId(),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.getDefaultClientId(),
                conditions: this.conditions({ isVisible: true }),
                order: 2,
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: String(this.model.id_usuario ?? this.user.id ?? ''),
                    isEditable: false,
                },
                width: 2,
                updateValue: () => String(this.model.id_usuario ?? this.user.id ?? ''),
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
            {
                name: 'adjunto_guia',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.adjunto_guia,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions({ isVisible: true }),
                order: 4,
            },
            {
                name: 'nombre_mantenimiento',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                updateValue: () => this.getSelectedActividadName(),
                conditions: this.conditions({ isVisible: true }),
                order: 5,
            },
            {
                name: 'nombre_especie',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                },
                width: 2,
                updateValue: () => getDropdownSelectedSingle(this.especiesAnimales)?.especies_animales ?? this.model.nombre_especie ?? '',
                conditions: this.conditions({ isVisible: true }),
                order: 6,
            },
        ];
    }

    private calendarField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
        range?: CalendarDateLimits,
    ): FormField<AgroambientalModel> {
        return {
            name,
            label,
            input: {
                type: this.formType < FormType.DELETION ? InputType.CALENDAR : InputType.ONLY_TEXT,
                isEditable: true,
                ...(this.formType < FormType.DELETION ? {
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                        ...(range?.minDate ? { minDate: range.minDate } : {}),
                        ...(range?.maxDate ? { maxDate: range.maxDate } : {}),
                        ...(range?.disabledDates ? { disabledDates: range.disabledDates } : {}),
                    },
                } : {}),
            },
            width,
            conditions: this.conditions({ isVisible: false }),
            dependencies: this.createTipoVisibilityDependencies(name),
            visibleWhen: this.visibleWhenFieldTipo(tipo),
            order,
        };
    }

    private textField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            label,
            input: { type: InputType.TEXT, isEditable: true },
            width,
            conditions: this.conditions({ isVisible: false }),
            dependencies: this.createTipoVisibilityDependencies(name),
            visibleWhen: this.visibleWhenFieldTipo(tipo),
            order,
        };
    }

    private numericField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            label,
            input: { type: this.formType < FormType.DELETION ? InputType.NUMERIC : InputType.ONLY_TEXT, isEditable: true },
            width,
            conditions: this.conditions({ isVisible: false }),
            dependencies: this.createTipoVisibilityDependencies(name),
            visibleWhen: this.visibleWhenFieldTipo(tipo),
            order,
        };
    }

    private textAreaField(
        name: keyof AgroambientalModel & string,
        label: string,
        order: number,
        tipo: string,
        width = 3,
    ): FormField<AgroambientalModel> {
        return {
            name,
            label,
            input: {
                type: this.formType < FormType.DELETION ? InputType.TEXTAREA : InputType.ONLY_TEXT,
                isEditable: true,
                ...(this.formType < FormType.DELETION ? {
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                } : {}),
            },
            width,
            conditions: this.conditions({ isVisible: false }),
            dependencies: this.createTipoVisibilityDependencies(name),
            visibleWhen: this.visibleWhenFieldTipo(tipo),
            order,
        };
    }

    private pastoreoRecommendationField(tipo: string, order: number): FormField<AgroambientalModel> {
        return {
            name: 'pastoreo_recommendation_help',
            label: '',
            input: { type: InputType.ONLY_TEXT, isEditable: false, style: 'display: none;' },
            helpText: '3 cortes al año en explotaciones con más del 50% de pasto en altitud igual o inferior a 300 metros.\n' +
                '2 cortes al año en explotaciones de pasto con altitud mayor de 300 metros.',
            width: 12,
            conditions: this.conditions({ isVisible: false }),
            dependencies: this.createTipoVisibilityDependencies('pastoreo_recommendation_help'),
            visibleWhen: this.visibleWhenFieldTipo(tipo),
            order,
        };
    }

    private pushFields(fields: FormField<AgroambientalModel>[]): FormField<AgroambientalModel>[] {
        for (const field of fields) {
            this.ensureTipoEcorregimenDependency(field);

            if (field.input.type !== InputType.HIDDEN && field.conditions.isVisible) {
                this.fieldNames.push(field);
                this.fieldLabels.push(field.label);
            }
        }

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private visibleWhenTipo(tipo: string): (context: ReactiveSectionLogicContext<AgroambientalModel>) => boolean {
        return (context): boolean => this.getTipoEcorregimen(context) === tipo;
    }

    private visibleWhenFieldTipo(tipo: string): NonNullable<FormField<AgroambientalModel>['visibleWhen']> {
        return {
            dependsOn: ['tipo_ecorregimen'],
            resolve: (context): boolean => this.formType === FormType.EXPORT || this.getTipoEcorregimen(context) === tipo,
        };
    }

    private createTipoVisibilityDependencies(
        target: string,
    ): NonNullable<FormField<AgroambientalModel>['dependencies']> {
        return [
            {
                source: 'tipo_ecorregimen',
                target,
                kind: 'visibility',
            },
        ];
    }

    private ensureTipoEcorregimenDependency(field: FormField<AgroambientalModel>): void {
        if (!this.getFieldTipo(field)) {
            return;
        }
    }

    private getFieldTipo(field: FormField<AgroambientalModel>): string | undefined {
        const fieldName = String(field.name);

        if ([
            'fecha_establecimiento_cv',
            'anchura_cv',
            'tipo_mantenimiento_cv',
            'anchura_libre_proyeccion_1',
            'fecha_establecimiento_ci',
            'anchura_ci',
            'anchura_libre_proyeccion_2',
        ].includes(fieldName)) {
            return 'Leñosos';
        }

        if ([
            'fecha_inicio_pastoreo_extensivo',
            'fecha_fin_pastoreo_extensivo',
            'especie',
            'nombre_especie',
            'rega',
            'numero_animales',
            'fecha_siega_1',
            'fecha_siega_2',
            'fecha_siega_3',
            'fecha_laboreo',
            'otras_actividades',
        ].includes(fieldName)) {
            return 'Pastoreo';
        }

        if ([
            'fecha_siembra',
            'dosis_siembra',
            'fecha_arroz',
            'fecha_nivelacion',
            'fecha_seca_inicio_intermitente',
            'fecha_seca_fin_intermitente',
            'fecha_inicio_inundacion_cultivo',
            'fecha_fin_inundacion_cultivo',
            'fecha_inicio_inundacion_invernal',
            'fecha_fin_inundacion_invernal',
            'labor',
            'tipo_operacion',
            'id_maquinaria',
            'maquinaria',
            'observaciones',
        ].includes(fieldName)) {
            return 'Arroz';
        }

        return undefined;
    }

    private getFieldByName(fieldName: string): FormField<AgroambientalModel> | undefined {
        return this.formFields.sections
            .flatMap((section) => section.fields)
            .find((field) => field.name === fieldName);
    }

    private isFincaOptionAllowed(
        value: unknown,
        context: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        return this.filterFinca(value as FincasModel | null, undefined, context);
    }

    private isSectorOptionForSelectedFinca(
        value: unknown,
        context: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        return this.filterSector(value as SectorModel | null, undefined, context);
    }

    private isParcelaOptionForSelectedLocation(
        value: unknown,
        context: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        return this.filterParcela(value as ParcelasModel | null, undefined, context);
    }

    private filterFinca(
        finca: FincasModel | null,
        context?: DropdownFilterContext<AgroambientalModel>,
        dataSourceContext?: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        if (finca === null) {
            return true;
        }

        if (!this.hasClients || this.formType === FormType.EDITION || !this.userCanSeeClientes) {
            return true;
        }

        const currentFincaId = this.getEntityId(this.getContextValue(context, 'id_finca', this.model.id_finca));

        if (currentFincaId.length > 0 && String(finca.id ?? '') === currentFincaId) {
            return true;
        }

        const clientId = this.getSelectedClientId(context, dataSourceContext);

        if (clientId.length === 0) {
            return true;
        }

        return String(finca.id_cliente ?? '') === clientId;
    }

    private filterSector(
        sector: SectorModel | null,
        context?: DropdownFilterContext<AgroambientalModel>,
        dataSourceContext?: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        if (sector === null) {
            return true;
        }

        const fincaId = this.getEntityId(
            dataSourceContext !== undefined
                ? getContextEntityId(dataSourceContext, 'id_finca', this.model.id_finca)
                : this.getContextValue(context, 'id_finca', this.model.id_finca),
        );

        return fincaId.length > 0 && String(sector.id_finca ?? '') === fincaId;
    }

    private filterParcela(
        parcela: ParcelasModel | null,
        context?: DropdownFilterContext<AgroambientalModel>,
        dataSourceContext?: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): boolean {
        if (parcela === null) {
            return true;
        }

        if (parcela.activo?.toString() !== '1') {
            return false;
        }

        const selectedSectorIds = dataSourceContext !== undefined
            ? getContextEntityIds(dataSourceContext, 'id_sector')
            : this.getSelectedSectorIds(context);

        if (selectedSectorIds.length > 0) {
            return selectedSectorIds.includes(String(parcela.id_sector ?? ''));
        }

        const fincaId = this.getEntityId(
            dataSourceContext !== undefined
                ? getContextEntityId(dataSourceContext, 'id_finca', this.model.id_finca)
                : this.getContextValue(context, 'id_finca', this.model.id_finca),
        );

        return fincaId.length > 0 && String(parcela.id_finca ?? '') === fincaId;
    }

    private getSelectedSectorIds(context?: DropdownFilterContext<AgroambientalModel>): string[] {
        return this.getReactiveValueTokens(this.getContextValue(context, 'id_sector', this.model.id_sector));
    }

    private getSelectedClientId(
        context?: DropdownFilterContext<AgroambientalModel>,
        dataSourceContext?: ReactiveDropdownDataSourceContext<AgroambientalModel>,
    ): string {
        if (dataSourceContext !== undefined) {
            return this.getEntityId(getContextEntityId(dataSourceContext, 'id_cliente', this.model.id_cliente ?? this.client));
        }

        return this.getEntityId(this.getContextValue(context, 'id_cliente', this.model.id_cliente ?? this.client));
    }

    private getContextValue(
        context: DropdownFilterContext<AgroambientalModel> | undefined,
        fieldName: keyof AgroambientalModel,
        fallback: unknown,
    ): unknown {
        return context?.model.get(String(fieldName))?.value ?? fallback;
    }

    private getEntityId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { readonly id?: unknown }).id ?? '').trim();
        }

        return String(value ?? '').trim();
    }

    private getReactiveValueTokens(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.flatMap((item) => this.getReactiveValueTokens(item));
        }

        if (typeof value === 'object' && value !== null) {
            const entityId = this.getEntityId(value);

            return entityId.length > 0 ? [entityId] : [];
        }

        return String(value ?? '')
            .split(/[;,]/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0);
    }

    private getParcelaLabel(p: ParcelasModel): string {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev ? `${variedad} - ${zonaRiegoAbrev}` : variedad;
        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficie = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficieFormatted = superficie.toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        const unidades = environment.features.textoSuperficie || 'ha';
        const rightText = [codigos || undefined, `${superficieFormatted} ${unidades}`]
            .filter(Boolean)
            .join(' · ');

        return `${nombre} - ${variedadConZona} — ${rightText}`;
    }

    private getSelectedActividadName(): string {
        const selected = getDropdownSelectedSingle(this.actividadesSobreCubierta);

        return selected?.actividad ?? selected?.actividad_sobre_la_cubierta ?? this.model.nombre_mantenimiento ?? '';
    }

    private getTipoEcorregimen(
        context?:
            Pick<ReactiveFieldLogicContext<AgroambientalModel>, 'getValue'> |
            Pick<ReactiveSectionLogicContext<AgroambientalModel>, 'getValue'>,
    ): string {
        const value = context?.getValue('tipo_ecorregimen') ?? this.model.tipo_ecorregimen;

        if (typeof value === 'string') {
            return value;
        }

        return '';
    }

    private getDefaultClientId(): string | undefined {
        if (StorageManager.getUser().tipo === 'comunero') {
            return String(StorageManager.getUser().id_cliente ?? '');
        }

        const clientIdFromForm = this.model.id_cliente;

        if (typeof clientIdFromForm === 'string' || typeof clientIdFromForm === 'number') {
            return String(clientIdFromForm);
        }

        if (clientIdFromForm && typeof clientIdFromForm === 'object' && 'id' in clientIdFromForm) {
            return String(clientIdFromForm.id ?? '');
        }

        return String(this.client?.id ?? '');
    }

    private getInundacionCalendarLimits(
        type: 'inicioCultivo' | 'finCultivo' | 'inicioInvernal' | 'finInvernal',
    ): CalendarDateLimits | undefined {
        if (this.appName !== 'avafitogest' || this.formType >= FormType.DELETION) {
            return undefined;
        }

        switch (type) {
        case 'inicioCultivo':
            return this.getAnnualCalendarLimits({ startMonth: 4, startDay: 5, endMonth: 4, endDay: 14 });
        case 'finCultivo':
            return this.getAnnualCalendarLimits({ startMonth: 7, startDay: 16, endMonth: 7, endDay: 24 });
        case 'inicioInvernal':
            return this.getAnnualCalendarLimits({ startMonth: 10, startDay: 4, endMonth: 10, endDay: 14 });
        case 'finInvernal':
            return this.getAnnualCalendarLimits({ startMonth: 0, startDay: 16, endMonth: 0, endDay: 25 });
        }
    }

    private getLenosCalendarLimits(type: 'cv' | 'ci'): CalendarDateLimits | undefined {
        if (this.appName !== 'avafitogest' || this.formType >= FormType.DELETION) {
            return undefined;
        }

        if (type === 'cv') {
            return this.getAnnualCalendarLimits({ startMonth: 2, startDay: 15, endMonth: 10, endDay: 15 });
        }

        return this.getAnnualCalendarLimits({ startMonth: 0, startDay: 31, endMonth: 4, endDay: 15 });
    }

    private getAnnualCalendarLimits(window: AnnualCalendarWindow): CalendarDateLimits {
        const currentYear = new Date().getFullYear();
        const pastYears = 10;
        const futureYears = 10;
        const minDate = new Date(currentYear - pastYears, 0, 1);
        const maxDate = new Date(currentYear + futureYears, 11, 31);
        const disabledDates = this.getDisabledDatesOutsideAnnualWindow(minDate, maxDate, window);

        return { minDate, maxDate, disabledDates };
    }

    private getDisabledDatesOutsideAnnualWindow(
        minDate: Date,
        maxDate: Date,
        window: AnnualCalendarWindow,
    ): Date[] {
        const disabledDates: Date[] = [];
        const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());

        while (cursor <= maxDate) {
            if (!this.isInsideAnnualCalendarWindow(cursor, window)) {
                disabledDates.push(new Date(cursor));
            }

            cursor.setDate(cursor.getDate() + 1);
        }

        return disabledDates;
    }

    private isInsideAnnualCalendarWindow(date: Date, window: AnnualCalendarWindow): boolean {
        const month = date.getMonth();
        const day = date.getDate();
        const datePosition = (month * 100) + day;
        const startPosition = (window.startMonth * 100) + window.startDay;
        const endPosition = (window.endMonth * 100) + window.endDay;

        if (startPosition <= endPosition) {
            return datePosition >= startPosition && datePosition <= endPosition;
        }

        return datePosition >= startPosition || datePosition <= endPosition;
    }

    private ensureDefaultDropdownValues(): void {
        if (this.tipoEcorregimenList.values.length === 0) {
            this.tipoEcorregimenList = list<string>('...', 'Leñosos', 'Arroz', 'Pastoreo');
        }
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
